/**
 * @license Copyright (c) 2003-2019, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Autosave from '@ckeditor/ckeditor5-autosave/src/autosave';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Link from '@ckeditor/ckeditor5-link/src/link';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import { Table } from '@ckeditor/ckeditor5-table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import SelectAll from '@ckeditor/ckeditor5-select-all/src/selectall';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters';
import SpecialCharactersEssentials from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials';
import WProofreader from '@webspellchecker/wproofreader-ckeditor5/src/wproofreader';
import TrackChanges from '@ckeditor/ckeditor5-track-changes/src/trackchanges';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import Comments from '@ckeditor/ckeditor5-comments/src/comments';
import { SourceEditingEnhanced } from '@ckeditor/ckeditor5-source-editing-enhanced';
import { List, ListProperties } from '@ckeditor/ckeditor5-list';

import TemplateHoverAndSelectAll from '../plugins/templatehoverandselectall/templatehoverandselectall';
import Smallcaps from '../plugins/smallcaps/smallcaps';
import Heading from '../plugins/heading/src/heading';
import Save from '../plugins/save/save';
import PasteWithoutStyletag from '../plugins/pastewithoutstyletag/pastewithoutstyletag';
import Indentation from '../plugins/indentation/indentation';
import Cut from '../plugins/cut/cut';
import Span from '../plugins/span/span';
import CustomAttributes from '../plugins/customattributes/customattributes';
import Citation from '../plugins/citations/citations';
import Paste from '../plugins/paste/paste';
import NumberedCitation from '../plugins/numberedcitation/numberedcitation';
import RenumberCitations from '../plugins/renumbercitations/renumbercitations';
import CopyCitationReference from '../plugins/copycitationreference/copycitationreference';
import DeleteCitations from '../plugins/deletecitations/deletecitations';
import Deletewebemaillink from '../plugins/deletewebemaillink/deletewebemaillink';
import SetCursorPositionRight from '../plugins/setcursorpositionright/setcursorpositionright';
import RestrictedHtml from '../plugins/restrictedhtml/restrictedhtml';
import CitationRefNav from '../plugins/citationrefnav/citationrefnav';
import ChevronNav from '../plugins/chevronnav/chevronnav';
import SetCursorPosition from '../plugins/setcursorposition/setcursorposition';
import SubTemplates from '../plugins/subtemplate/subtemplates';
import InsertSubTemplate from '../plugins/insertsubtemplate/insertsubtemplate';
import Contextmenu from '../plugins/contextmenu/contextmenu';
import CitationsDeletionFromDocument from '../plugins/citationsdeletionfromdocument/citationsdeletionfromdocument';
import CitationsReOrder from '../plugins/citationsreorder/citationsreorder';
import CitationsCitedRefsReorder from '../plugins/citationscitedrefsreorder/citationscitedrefsreorder';
import GenerateCustomGuid from '../plugins/generatecustomguid/generatecustomguid';
import NumberedCitationConverter from '../plugins/numberedcitationconverter/numberedcitationconverter';
import InsertMedia from '../plugins/insertmedia/insertmedia';
import ReOrderCopiedCitation from '../plugins/reordercopiedcitation/reordercopiedcitation';
import CopyCitationNotification from '../plugins/copycitationnotification/copycitationnotification';
import InsertImage from '../plugins/insertimage/insertimage';
import UpdateMedia from '../plugins/updatemedia/updatemedia';
import MediaNotification from '../plugins/medianotification/medianotification';
import Figure from '../plugins/figure/figure';
import Video from '../plugins/video/video';
import Source from '../plugins/source/source';
import InsertVideo from '../plugins/insertvideo/insertvideo';
import TrackChangesForKeywords from '../plugins/trackchangesforkeywords/trackchangesforkeywords';
import TrackChangesAndCommentsIntegration from '../plugins/trackchangesandcommentsintegration/trackchangesandcommentsintegration';
import InsertLink from '../plugins/insertLink/insertlink';
import InsertReference from '../plugins/insertreference/insertreference';
import InsertWebEmailLink from '../plugins/insertwebemaillink/insertwebemaillink';
import InsertOtherDocumentLink from '../plugins/insertotherdocumentlink/insertotherdocumentlink';
import InsertFragmentLink from '../plugins/insertfragmentlink/insertfragmentlink';

import EditWebEmailLink from '../plugins/editwebemaillink/editwebemaillink';
import Audio from '../plugins/audio/audio';
import InsertAudio from '../plugins/insertaudio/insertaudio';
import OnEnterFigureTag from '../plugins/enterafterfiguretag/enterafterfiguretag';
import TemplateDocCompatibleTags from '../plugins/templatedoccompatibletags/templatedoccompatibletags';
import CustomPluginEnabler from '../plugins/custompluginenabler/custompluginenabler';
import Img from '../plugins/img/img';
import CustomBold from '../plugins/custombold/custombold';
import CustomCommentView from '../plugins/comments/customcommentview';
import CustomCommentThreadView from '../plugins/comments/customcommentthreadview';
import CustomTrackChangeView from '../plugins/comments/customtrackchangeview';
import ImgToolbar from '../plugins/imgtoolbar/imgtoolbar';
import ToggleCaption from '../plugins/tooglecaption/tooglecaption';
import FigCaptionTag from '../plugins/figcaption/figcaption';
import DisablePlugin from '../plugins/disableplugin/disableplugin';
import HideDataIgnoreAttribute from '../plugins/hidedataignoreattribute/hidedataignoreattribute';
import ReOrderReferences from '../plugins/reorderreferences/reorderreferences';
import InlineSubtemplateFormatting from '../plugins/inlinesubtemplateformatting/inlinesubtemplateformatting';
import RemoveMarkupForResolvedComment from '../plugins/comments/removemarkupforresolvedcomment';
import UpdatePlaceholder from '../plugins/updateplaceholder/updateplaceholder';
import CustomEventListeners from '../plugins/customeventlisteners/customeventlisteners';
import RemoveSubtemplate from '../plugins/removesubtemplate/removesubtemplate';
import RemoveISTSubtemplate from '../plugins/removeistsubtemplate/removeistsubtemplate';
import KeywordDefinition from '../plugins/keyworddefinition/keyworddefinition';
import TrackChangesSeparateSuggestions from '../plugins/trackchangesseparatesuggestions/trackchangesseparatesuggestions';
import KeywordDefinitionDelete from '../plugins/keyworddefinitiondelete/keyworddefinitiondelete';
import KeywordUndoRedo from '../plugins/keywordundoredo/keywordundoredo';
import KeywordDeletionFromDocument from '../plugins/keyworddeletionfromdocument/keyworddeletionfromdocument';
import { FindAndReplace } from '../plugins/ckeditor5-find-and-replace/src';
import AddSpecialCharacters from '../plugins/addspecialcharacters/addspecialcharacters';
import TrackChangesLabel from '../plugins/trackchangeslabel/trackchangeslabel';
import ReleaseNotes from '../plugins/releasenotes/releasenotes';
import WordCount from '../plugins/ckeditor5-word-count/src/wordcount';
import AIAssistant from '../plugins/ai-assistant/ai-assistant.js';
import DeleteInBatchById from '../plugins/deleteinbatchbyid/deleteinbatchbyid';
import UnwrapLink from '../plugins/unwraplink/unwraplink';
import SourceEditingAvailability from '../plugins/sourceeditingavailability/sourceeditingavailability';
import SourceEditingFindAndReplace from '../plugins/sourceeditingfindandreplace/sourceeditingfindandreplace';
import SourceEditingByReference from '../plugins/sourceeditingbyreference/sourceeditingbyreference';

export default class CKEditor extends ClassicEditorBase {
  static async create(sourceElementOrData, config = {}) {
    const editor = await super.create(sourceElementOrData, config);

    const cmd = editor.commands.get('editSource');
    if (cmd) {
      const originalForceDisabled = cmd.forceDisabled?.bind(cmd);
      if (originalForceDisabled) {
        cmd.forceDisabled = (lockId) => {
          if (!editor.isReadOnly) {
            return;
          }
          return originalForceDisabled(lockId);
        };
      }

      const keepEnabled = () => {
        if (editor.isReadOnly) {
          return;
        }
        const ids = cmd._disableStack ? [...cmd._disableStack] : [];
        ids.forEach(id => {
          if (id !== 'readOnlyMode') {
            cmd.clearForceDisabled?.(id);
          }
        });
        cmd.refresh?.();
        if (typeof cmd.set === 'function') {
          cmd.set('isEnabled', true);
        } else {
          cmd.isEnabled = true;
        }
      };

      keepEnabled();
      editor.plugins.get('PendingActions')?.on('change:hasAny', () => setTimeout(keepEnabled, 0));
      editor.model.document.on('change:data', keepEnabled);
    }

    return editor;
  }
}

// Plugins to include in the build.
CKEditor.builtinPlugins = [
  List,
  ListProperties,
  FindAndReplace,
  Essentials,
  Alignment,
  UploadAdapter,
  Autoformat,
  Bold,
  Comments,
  Italic,
  Underline,
  Strikethrough,
  CloudServices,
  Subscript,
  Superscript,
  BlockQuote,
  CKFinder,
  EasyImage,
  Heading,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Link,
  InsertLink,
  InsertReference,
  InsertWebEmailLink,
  InsertOtherDocumentLink,
  InsertFragmentLink,
  EditWebEmailLink,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableCaption,
  TableColumnResize,
  Autosave,
  TextTransformation,
  Smallcaps,
  Save,
  PasteWithoutStyletag,
  SelectAll,
  Indentation,
  Cut,
  RemoveFormat,
  Span,
  CustomAttributes,
  Citation,
  Paste,
  SpecialCharacters,
  NumberedCitation,
  SpecialCharactersEssentials,
  RenumberCitations,
  CopyCitationReference,
  DeleteCitations,
  Deletewebemaillink,
  SetCursorPositionRight,
  RestrictedHtml,
  TemplateHoverAndSelectAll,
  ChevronNav,
  CitationRefNav,
  SetCursorPosition,
  SubTemplates,
  InsertSubTemplate,
  Contextmenu,
  WProofreader,
  CitationsDeletionFromDocument,
  CitationsReOrder,
  CitationsCitedRefsReorder,
  GenerateCustomGuid,
  InsertMedia,
  ReOrderCopiedCitation,
  NumberedCitationConverter,
  CopyCitationNotification,
  InsertImage,
  UpdateMedia,
  MediaNotification,
  Figure,
  Video,
  Source,
  InsertVideo,
  TrackChanges,
  TrackChangesForKeywords,
  TrackChangesAndCommentsIntegration,
  Audio,
  InsertAudio,
  GeneralHtmlSupport,
  TemplateDocCompatibleTags,
  OnEnterFigureTag,
  CustomPluginEnabler,
  Img,
  CustomBold,
  ImgToolbar,
  ToggleCaption,
  FigCaptionTag,
  DisablePlugin,
  HideDataIgnoreAttribute,
  ReOrderReferences,
  InlineSubtemplateFormatting,
  RemoveMarkupForResolvedComment,
  UpdatePlaceholder,
  CustomEventListeners,
  RemoveSubtemplate,
  RemoveISTSubtemplate,
  KeywordDefinition,
  TrackChangesSeparateSuggestions,
  KeywordDefinitionDelete,
  KeywordUndoRedo,
  KeywordDeletionFromDocument,
  AddSpecialCharacters,
  SourceEditingEnhanced,
  TrackChangesLabel,
  ReleaseNotes,
  WordCount,
  AIAssistant,
  DeleteInBatchById,
  SourceEditingAvailability,
  SourceEditingFindAndReplace,
  SourceEditingByReference,
  UnwrapLink
];
// Editor configuration.
CKEditor.defaultConfig = {
  htmlSupport: {
    allow: [
      {
        name: /^(aside|article|section|div|header|p|ul|th|td)$/,
        attributes: true,
        classes: true,
        styles: true
      },
      {
        name: 'figure',
        attributes: true
      }, {
        name: 'table',
        attributes: true,
        classes: true
      }, {
        name: 'figcaption',
        attributes: true,
        classes: true,
        styles: true
      }
    ],
    disallow: [
      {
        name: 'table',
        attributes: /^(cellpadding|cellspacing|border|width)$/
      }
    ]
  },
  licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NzQzMTAzOTksImp0aSI6IjAxMjVmMjAwLWYwZDctNDcxMS1iOTZjLTJmYmFmZDQ5YzNhYyIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIlRDIiwiQ01UIiwiRE8iLCJGUCIsIlNDIiwiVE9DIiwiVFBMIiwiUE9FIiwiQ0MiLCJNRiIsIlNFRSIsIkVDSCIsIkVJUyIsIkxIIl0sInZjIjoiMmQ1OTY3NzgifQ.RCaQQkv86prlWmQqemXxA4P8eOegRPG1t8Z-PtlsLIxX5t3EZXpcVvpqO_Z15WIR2Xr615Vu8diousGltHyyCQ',
  image: {
    styles: [
      'block',
      'alignLeft',
      'alignRight'
    ],
    toolbar: [
      'imageStyle:alignLeft',
      'imageStyle:block',
      'imageStyle:alignRight',
      '|',
      'imageTextAlternative'
    ]
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
      'toggleTableCaption'
    ]
  },
  // This value must be kept in sync with the language defined in webpack.config.js.
  language: 'en',
  autosave: {
    waitingTime: 120000,
    save(editor) {
      return editor.commands.get('save').execute();
    }
  },
  wproofreader: {
    serviceProtocol: 'https',
    serviceHost: 'stg-wpr-us-apl.wkisatsysops.net',
    servicePort: '443',
    servicePath: '/',
    srcUrl: 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js'
  },
  removePlugins: ['EasyImage', 'Image', 'ImageCaption', 'ImageStyle', 'ImageToolbar', 'ImageUpload', 'CKFinder'],
  trackChanges: {
    SuggestionThreadView: CustomTrackChangeView,
    mergeNestedSuggestions: false
  },
  comments: {
    CommentView: CustomCommentView,
    CommentThreadView: CustomCommentThreadView,
    editorConfig: {
      extraPlugins: [Autoformat, Bold, Italic]
    }
  },
  locale: {
    dateTimeFormat: date => window._datePipe.transform(date, 'short')
  }
};

